/**
 * A printing's name, in words.
 *
 * Lives on its own, with no imports, because two other things need it: the app,
 * and the script that matches an owned card to its catalogue printing. If the
 * script carried its own copy of these labels the two would drift, and a drifted
 * label means a card silently resolving to the wrong printing - which is the
 * difference between a 1st Edition Charizard and an unlimited one.
 */

/**
 * Display names. Anything absent falls back to title case, so a value added to
 * the catalogue tomorrow still renders as something sane rather than breaking.
 */
const LABELS = {
  type: {
    normal: 'Normal',
    holo: 'Holo',
    reverse: 'Reverse Holo',
    metal: 'Metal',
    lenticular: 'Lenticular',
  },
  foil: {
    pokeball: 'Poke Ball',
    masterball: 'Master Ball',
    duskball: 'Dusk Ball',
    loveball: 'Love Ball',
    friendball: 'Friend Ball',
    quickball: 'Quick Ball',
    'cracked-ice': 'Cracked Ice',
    'team-rocket': 'Team Rocket',
    'player-reward': 'Player Reward',
    'professor-program': 'Professor Program',
  },
  subtype: {
    // The catalogue's own term. "4th Print" is not something collectors say and
    // is not what this identifier means - it is the copyright line on the card.
    '1999-2000-copyright': '1999-2000 Copyright',
    '1999-copyright': '1999 Copyright',
    'no-e-reader': 'No e-Reader Strip',
    'missing-expansion-symbol': 'Missing Set Symbol',
    'no-holo-error': 'Non-Holo Error',
  },
  stamp: {
    '1st-edition': '1st Edition',
    'pre-release': 'Prerelease',
    'set-logo': 'Set Logo',
    'w-promo': 'W Promo',
    'player-rewards-program': 'Player Rewards',
    '25th-celebration': '25th Celebration',
  },
};

function titleCase(value) {
  return String(value || '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function labelFor(axis, value) {
  if (!value) return '';
  return (LABELS[axis] && LABELS[axis][value]) || titleCase(value);
}

/**
 * Compose the label the catalogue itself uses, so what the app shows and what
 * the data says are the same sentence.
 *
 * @returns {string} e.g. "Holo / Shadowless / 1st Edition Stamp"
 */
export function variantLabel({ type, subtype, foil, stamps } = {}) {
  const parts = [];
  if (type) parts.push(labelFor('type', type));
  if (subtype) parts.push(labelFor('subtype', subtype));
  if (foil) parts.push(`${labelFor('foil', foil)} Foil`);
  (stamps || []).forEach((s) => parts.push(`${labelFor('stamp', s)} Stamp`));
  return parts.join(' / ');
}

export default { labelFor, variantLabel };

